import { useRecoilState } from "recoil";
import { accessTokenState, developerAccessTokenState } from "../global-state";
import { Amplify, Auth } from "aws-amplify";
import { USER_AUTH_CONFIG, DEV_AUTH_CONFIG } from "../aws-config";
import { SAND_CAT } from "../config";

const useApiCall = () => {
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState,
  );

  const apiCall = async (
    endpoint,
    method = "GET",
    body = null,
    role = "dev",
    retry = 0,
    oUserToken = null,
    oDevToken = null,
  ) => {
    if (retry >= 2) {
      console.warn(`Max retry attempts reached for endpoint: ${endpoint}`);
      return null;
    }

    try {
      const headers = {};
      const roleMap = {
        user: "Authorization",
        dev: "DeveloperAuthorization",
        org: "OrgAuthorization",
        admin: "AdminAuthorization",
      };

      const ut = oUserToken || accessToken;
      const dt = oDevToken || developerAccessToken;

      const roles = role.split(",");
      for (const r of roles) {
        if (r in roleMap) {
          const token = r === "user" ? ut : dt;
          headers[roleMap[r]] = token ? `Bearer ${token}` : "";
        }
      }

      const options = {
        method,
        headers,
      };

      if (body) {
        if (body instanceof FormData) {
          options.body = body;
        } else {
          options.body = JSON.stringify(body);
          headers["Content-Type"] = "application/json;charset=UTF-8";
        }
      }

      let baseUrl = SAND_CAT;
      const customAddress = localStorage.getItem("localServiceAddress");
      if (customAddress) {
        baseUrl = SAND_CAT.replace("localhost", customAddress);
      }

      const response = await fetch(`${baseUrl}${endpoint}`, options);
      if (
        response.status === 204 ||
        response.headers.get("content-length") === "0"
      ) {
        return { success: true };
      }

      const text = await response.text();

      const jsonData = text ? JSON.parse(text) : { success: true };

      if (!response.ok) {
        if (jsonData.error === "UNAUTHENTICATED") {
          console.warn("Token expired. Attempting refresh...");
          const ut = await refreshUserToken();
          const dt = await refreshDeveloperToken();
          return await apiCall(endpoint, method, body, role, retry + 1, ut, dt);
        }
        throw new Error(jsonData);
      }

      return jsonData;
    } catch (error) {
      console.error(`Error during ${method} request to ${endpoint}:`, error);
      return null;
    }
  };

  const refreshUserToken = async () => {
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      setAccessToken(accessToken);
      return accessToken;
    } catch (error) {
      console.error("Error refreshing user token:", error);
      throw error;
    }
  };

  const refreshDeveloperToken = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      setDeveloperAccessToken(accessToken);
      return accessToken;
    } catch (error) {
      console.warn(
        "Error refreshing developer token; ignoring if not logged in.",
        error,
      );
      return null;
    }
  };

  return { apiCall, refreshUserToken, refreshDeveloperToken };
};

export default useApiCall;
